import type {
  MediaFileAsset,
  SpeechAsset,
  StsScriptAsset,
  TtsScriptAsset,
  VoiceAsset,
} from '../../../../audio-form/data/dto/audio-form-input.ts'
import { type TracksStore, TrackType } from '@/shared/store/tracks.store.tsx'
import type { Clip } from '../../models/clip.ts'
import { useSpeechStore } from '@/shared/store/speech.store.ts'
import type { SpeechData } from '@/features/session/domain/models/session.ts'
import { useScriptStore } from '@/shared/store/script.store.ts'

type VoiceOrSpeechAssets = Record<
  string,
  MediaFileAsset | SpeechAsset | StsScriptAsset | TtsScriptAsset | VoiceAsset
>

const getVoiceAlias = (speech: SpeechData): string => speech.voice.id

const getOrCreateVoiceRef = (
  voiceAlias: string,
  speech: SpeechData,
  assets: VoiceOrSpeechAssets,
  voiceAliasToRef: Map<string, string>,
  voiceRefIndex: { value: number }
): string => {
  const speed = speech.speed
  const voicePreset = speech.expressive ? 'expressive' : 'standard'
  const cacheKey = `${voiceAlias}:${speed}:${voicePreset}`
  let voiceRef = voiceAliasToRef.get(cacheKey)

  if (voiceRef === undefined) {
    voiceRef = `voice-ref-${voiceRefIndex.value++}`
    voiceAliasToRef.set(cacheKey, voiceRef)
    assets[voiceRef] = {
      type: 'voice',
      voiceAlias,
      speed: speech.speed,
      voicePreset,
    } satisfies VoiceAsset
  }

  return voiceRef
}

const addStsAsset = (
  assets: VoiceOrSpeechAssets,
  mediaId: string,
  fileId: string,
  voiceRef: string,
  src?: string
): void => {
  assets[fileId] ??= {
    type: 'media',
    fileId,
  } satisfies MediaFileAsset
  assets[mediaId] = {
    type: 'sts',
    mediaRef: fileId,
    voiceRef,
    ...(src && { uri: src }),
  } satisfies StsScriptAsset
}

const addMediaAsset = (
  assets: VoiceOrSpeechAssets,
  mediaId: string,
  src?: string
): void => {
  assets[mediaId] = {
    type: 'media',
    fileId: mediaId,
    ...(src && { uri: src }),
  }
}

const addSpeechAsset = (
  assets: VoiceOrSpeechAssets,
  mediaId: string,
  src?: string
): void => {
  assets[mediaId] = {
    type: 'speech',
    speechId: mediaId,
    ...(src && { uri: src }),
  }
}

function processSpeechClip(
  clip: Clip,
  speech: SpeechData | undefined,
  effectiveType: 'tts' | 'sts' | undefined,
  assets: VoiceOrSpeechAssets,
  scripts: Array<{ id: string; text: string }>,
  voiceAliasToRef: Map<string, string>,
  voiceRefIndex: { value: number }
): void {
  const { mediaId, src } = clip

  if (effectiveType === 'sts' && speech?.fileId) {
    const voiceAlias = getVoiceAlias(speech)
    const voiceRef = getOrCreateVoiceRef(
      voiceAlias,
      speech,
      assets,
      voiceAliasToRef,
      voiceRefIndex
    )
    addStsAsset(assets, mediaId, speech.fileId, voiceRef, src)
    return
  }

  if (effectiveType === 'sts') {
    addMediaAsset(assets, mediaId, src)
    return
  }

  if (effectiveType === 'tts') {
    const script = scripts.find((item) => item.id === speech?.scriptId)
    if (speech?.anchor && script) {
      const voiceAlias = getVoiceAlias(speech)
      const voiceRef = getOrCreateVoiceRef(
        voiceAlias,
        speech,
        assets,
        voiceAliasToRef,
        voiceRefIndex
      )
      assets[mediaId] = {
        type: 'tts',
        text: script.text,
        voiceRef,
        anchor: speech.anchor,
        ...(src && { uri: src }),
      }
      return
    }
    addSpeechAsset(assets, mediaId, src)
    return
  }

  addSpeechAsset(assets, mediaId, src)
}

export const mapVoiceAndSpeechClipsToAssets = (
  tracksStore: TracksStore
): VoiceOrSpeechAssets => {
  const assets: VoiceOrSpeechAssets = {}
  const speechesFromStore = useSpeechStore.getState().speeches
  const scripts = useScriptStore.getState().scripts

  const speeches =
    tracksStore.tracks.find((track) => track.type === TrackType.Voices)
      ?.clips ?? []

  const speechClips = speeches.filter(
    (clip): clip is Clip => clip.startAt !== undefined
  )

  const voiceAliasToRef = new Map<string, string>()
  const voiceRefIndex = { value: 0 }

  for (const clip of speechClips) {
    const speech = speechesFromStore[clip.mediaId]
    const effectiveType = clip.speechType ?? speech?.type

    processSpeechClip(
      clip,
      speech,
      effectiveType,
      assets,
      scripts,
      voiceAliasToRef,
      voiceRefIndex
    )
  }

  return assets
}
